import { GetServerSideProps } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormEvent, useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'

import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import Panel from '@/components/Panel/Panel'
import SimpleHeader from '@/components/SimpleHeader/SimpleHeader'
import Spinner from '@/components/Spinner/Spinner'
import { useAds } from '@/contexts/ads'
import profileService from '@/services/profile.service'
import { gdprAffectedCountries } from '@/utils/constants'
import { redirectOnLogin } from '@/utils/helpers'

type Props = {
  initCountry?: string
}

const SignUp = () => {
  const { country } = useAds()
  const router = useRouter()
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [consents, setConsents] = useState({ newsletter: false })
  const [email, setEmail] = useState('')
  const [passwordOne, setPasswordOne] = useState('')
  const [passwordTwo, setPasswordTwo] = useState('')
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (country && !gdprAffectedCountries.includes(country)) {
      setConsents({ newsletter: true })
    }
  }, [country])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (passwordOne !== passwordTwo) {
      setError('Passwords must match')
    } else {
      const password = passwordOne
      setIsLoading(true)
      profileService
        .register({ consents, email, password, username })
        .then((data) => {
          if (data.error) return setError(data.error)
          setSuccess(true)
          profileService
            .login(email, password)
            .then(() => redirectOnLogin(router))
            .catch((err) => setError(err.message))
        })
        .catch((err) => setError(err.message))
        .finally(() => setIsLoading(false))
    }
  }

  return (
    <Main>
      <Container small>
        <ExplicitHead description='EDHREC Sign Up' keywords='EDHREC,sign,up' title='Sign Up | EDHREC' />
        <SimpleHeader>Sign Up</SimpleHeader>
        <Panel>
          <form className='d-grid gap-3 pb-3' onSubmit={onSubmit}>
            <FloatingLabel label='Email'>
              <Form.Control
                onChange={(e) => setEmail(e.target.value)}
                placeholder='Email'
                required
                type='email'
                value={email}
              />
            </FloatingLabel>
            <FloatingLabel label='Username'>
              <Form.Control
                onChange={(e) => setUsername(e.target.value)}
                placeholder='Username'
                required
                type='text'
                value={username}
              />
            </FloatingLabel>
            <FloatingLabel label='Password'>
              <Form.Control
                onChange={(e) => setPasswordOne(e.target.value)}
                placeholder='Password'
                required
                type='password'
                value={passwordOne}
              />
            </FloatingLabel>
            <FloatingLabel label='Confirm Password'>
              <Form.Control
                onChange={(e) => setPasswordTwo(e.target.value)}
                placeholder='Confirm Password'
                required
                type='password'
                value={passwordTwo}
              />
            </FloatingLabel>
            <Form.Group>
              <Form.Check
                checked={consents.newsletter}
                id='newsletterConsent'
                label='Yes, I would like to receive the EDHREC newsletter.'
                onChange={() => setConsents({ newsletter: !consents.newsletter })}
                type='checkbox'
              />
            </Form.Group>
            <Button disabled={isLoading} size='lg' type='submit' variant='primary'>
              {isLoading ? <Spinner /> : 'Sign Up'}
            </Button>
            {error && <Alert variant='danger'>{error}</Alert>}
            {success && <Alert variant='success'>Registration successful, redirecting...</Alert>}
            <Link className='m-auto' href='/signin'>
              I already have an account.
            </Link>
          </form>
        </Panel>
      </Container>
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  const props: Props = {}
  if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
  return { props }
}

export default SignUp
